// src/pages/PatientReschedulePage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Paper, Typography, CircularProgress, Alert, Button,
  List, ListItem, ListItemButton, ListItemText, Divider, Avatar,
  Chip, Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

// ============== Helpers TZ-aware (sin dependencias externas) ==============
const getBrowserTZ = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const toDate = (d) => new Date(d);

// Si llega "YYYY-MM-DD", añadimos mediodía para evitar saltos raros por TZ
const coerceDateForDayKey = (d) => {
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return new Date(`${d}T12:00:00Z`);
  }
  return new Date(d);
};

const fmtDateTZ = (d, tz) =>
  new Intl.DateTimeFormat('es-ES', {
    timeZone: tz,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(coerceDateForDayKey(d));

const fmtTimeTZ = (d, tz) =>
  new Intl.DateTimeFormat('es-ES', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(toDate(d));

const getHourInTZ = (d, tz) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    hour12: false
  }).formatToParts(toDate(d));
  const hourPart = parts.find((p) => p.type === 'hour');
  return hourPart ? parseInt(hourPart.value, 10) : toDate(d).getUTCHours();
};

const isMorningTZ = (d, tz) => { const h = getHourInTZ(d, tz); return h >= 6 && h < 12; };
const isAfternoonTZ = (d, tz) => { const h = getHourInTZ(d, tz); return h >= 12 && h < 18; };
const isEveningTZ = (d, tz) => { const h = getHourInTZ(d, tz); return h >= 18 && h <= 23; };

const timePassesFilter = (slot, filter, tz) => {
  if (filter === 'all') return true;
  if (filter === 'morning') return isMorningTZ(slot.start, tz);
  if (filter === 'afternoon') return isAfternoonTZ(slot.start, tz);
  if (filter === 'evening') return isEveningTZ(slot.start, tz);
  return true;
};

// ========================== Componente principal ==========================
const PatientReschedulePage = () => {
  const theme = useTheme();
  const { id } = useParams(); // id_cita
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [appt, setAppt] = useState(null);
  const [slots, setSlots] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all'); // all | morning | afternoon | evening
  const [tz, setTz] = useState(getBrowserTZ()); // zona horaria a usar en toda la pantalla
  const [tzSource, setTzSource] = useState('browser'); // 'browser' | 'backend'

  // 1) Cargar la cita (para obtener medico_id, etc.)
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get(`/patients/appointments/${id}/`);
        if (cancel) return;
        setAppt(data);
      } catch (e) {
        if (cancel) return;
        setError(e?.response?.data?.detail || 'No se pudo cargar la cita');
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [id]);

  // 2) Cargar slots del mismo médico (próximos 30 días), tal como fueron configurados
  useEffect(() => {
    if (!appt?.medico_id) return;
    let cancel = false;
    const loadSlots = async () => {
      try {
        setError('');
        // Pedimos slots en la ventana siguiente a "ahora" (30 días)
        const start = new Date().toISOString();
        const end = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

        const { data } = await axiosInstance.get(`/doctors/${appt.medico_id}/available-slots/`, {
          params: { start, end }
        });

        // Respuesta: lista simple o { timezone, slots }
        const list = Array.isArray(data) ? data : (data?.slots || []);
        const responseTZ = !Array.isArray(data) && data?.timezone ? data.timezone : null;
        if (responseTZ) {
          setTz(responseTZ);
          setTzSource('backend');
        } else {
          setTz(getBrowserTZ());
          setTzSource('browser');
        }

        if (cancel) return;

        // Orden ascendente global (garantiza que ej. 07:00 salga antes que 18:15)
        list.sort((a, b) => new Date(a.start) - new Date(b.start));
        setSlots(list);

        // Si backend envía timezone, úsala para agrupar/mostrar de forma consistente
        if (responseTZ) setTz(responseTZ);
      } catch (e) {
        if (cancel) return;
        setError('No se pudieron cargar los horarios disponibles.');
      }
    };
    loadSlots();
    return () => { cancel = true; };
  }, [appt?.medico_id]);

  // 3) Agrupar por fecha en la TZ (no en UTC)
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      // Día en formato ISO YYYY-MM-DD de la TZ elegida (en-CA => 2025-10-23)
      const dayKey = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit'
      }).format(new Date(s.start));
      if (!map.has(dayKey)) map.set(dayKey, []);
      map.get(dayKey).push(s);
    }
    // Ordenar slots del día por hora ascendente
    for (const [, arr] of map.entries()) {
      arr.sort((a, b) => new Date(a.start) - new Date(b.start));
    }
    // Retornar en orden de fecha ascendente por clave
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  }, [slots, tz]);

  const openConfirm = (slot) => {
    setSelected(slot);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setSelected(null);
  };

  const confirm = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      await axiosInstance.patch(`/patients/appointments/${id}/`, {
        fecha_hora_cita: selected.start
      });
      navigate('/patient/appointments');
    } catch (e) {
      setError(
        e?.response?.data?.fecha_hora_cita?.[0] ||
        e?.response?.data?.error ||
        'No se pudo reprogramar la cita'
      );
    } finally {
      setSaving(false);
      closeConfirm();
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '50vh', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexDirection: 'column' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Cargando...</Typography>
      </Box>
    );
  }

  const dayCardBorder = `1px solid ${alpha(theme.palette.primary.main, 0.25)}`;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{
        p: 3,
        borderRadius: 4,
        bgcolor: 'background.paper',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.12)}`
      }}>
        {/* Header con tono M3 y colores */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          mb: 2, gap: 2, flexWrap: 'wrap',
          p: 2,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.4)})`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main
            }}>
              <CalendarMonthIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Reprogramar cita
              </Typography>
              {appt && (
                <Typography variant="body2" color="text.secondary">
                  {appt.medico_nombre} • {appt.medico_especialidad}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Filtros por franja + chip de zona horaria */}
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Tooltip title="Filtrar por franja horaria">
              <Chip icon={<FilterAltIcon />} label="Filtros" variant="outlined" />
            </Tooltip>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(_, v) => v && setFilter(v)}
              size="small"
              color="primary"
            >
              <ToggleButton value="all">Todo</ToggleButton>
              <ToggleButton value="morning">Mañana</ToggleButton>
              <ToggleButton value="afternoon">Tarde</ToggleButton>
              <ToggleButton value="evening">Noche</ToggleButton>
            </ToggleButtonGroup>
            <Chip size="small" variant="outlined" 
            label={tzSource === 'backend'
              ? `Horario de la clínica: ${tz}`
              : `Zona horaria del dispositivo: ${tz}`
            } 
          />
          </Stack>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Info de la cita actual */}
        {appt && (
          <Box sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 2
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EventIcon fontSize="small" color="action" />
              <Typography variant="body2">
                Actual: {fmtDateTZ(appt.fecha_hora_cita, tz)} · {fmtTimeTZ(appt.fecha_hora_cita, tz)}
              </Typography>
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Listado de slots por día (solo días con disponibilidad) */}
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay horarios disponibles en los próximos 30 días.
          </Typography>
        ) : (
          <Box>
            {groups.map(([dateKey, daySlots]) => {
              // Filtrar por franja horaria usando la TZ
              const filtered = daySlots.filter(s => timePassesFilter(s, filter, tz));
              if (filtered.length === 0) return null;

              return (
                <Box
                  key={dateKey}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 3,
                    border: dayCardBorder,
                    background: `linear-gradient(180deg, ${alpha(theme.palette.secondary.light, 0.25)}, ${alpha(theme.palette.background.paper, 1)})`
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1.5, color: theme.palette.primary.main,
                      fontWeight: 700, textTransform: 'capitalize'
                    }}
                  >
                    {fmtDateTZ(dateKey, tz)}
                  </Typography>

                  <List dense disablePadding>
                    {filtered.map((s) => {
                      const start = s.start;
                      const end = s.end;
                      const dur = s.duration || Math.round((new Date(end) - new Date(start)) / 60000);

                      const isM = isMorningTZ(start, tz);
                      const isA = isAfternoonTZ(start, tz);
                      const isE = isEveningTZ(start, tz);

                      const baseBg = isM
                        ? alpha(theme.palette.success.main, 0.08)
                        : isA
                          ? alpha(theme.palette.info.main, 0.08)
                          : alpha(theme.palette.warning.main, 0.08);

                      return (
                        <ListItem key={start} disableGutters sx={{ mb: 1 }}>
                          <ListItemButton
                            onClick={() => openConfirm(s)}
                            sx={{
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                              backgroundColor: baseBg,
                              transition: 'transform .15s ease, box-shadow .2s ease, border-color .15s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.18)}`,
                                borderColor: theme.palette.primary.main,
                                backgroundColor: alpha(baseBg, 0.9),
                              }
                            }}
                          >
                            <ListItemText
                              primaryTypographyProps={{ variant: 'body1', fontWeight: 800, component: 'div' }}
                              primary={`${fmtTimeTZ(start, tz)} – ${fmtTimeTZ(end, tz)}`}
                              secondaryTypographyProps={{ component: 'div' }}
                              secondary={
                                <Stack component="div" direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                  <Chip
                                    size="small"
                                    label={`Duración: ${dur} min`}
                                    sx={{
                                      borderRadius: 1.5,
                                      bgcolor: alpha(theme.palette.secondary.main, 0.2),
                                      color: theme.palette.primary.dark,
                                      fontWeight: 700
                                    }}
                                  />
                                </Stack>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Footer de acciones */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/patient/appointments')} sx={{ borderRadius: 2 }}>
            Cancelar
          </Button>
        </Box>
      </Paper>

      {/* Diálogo de confirmación */}
      <Dialog open={confirmOpen} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar nueva hora</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tu cita con {appt?.medico_nombre} será reprogramada a:
              </Typography>
              <Typography variant="h6" fontWeight={800}>
                {fmtDateTZ(selected.start, tz)} · {fmtTimeTZ(selected.start, tz)} – {fmtTimeTZ(selected.end, tz)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Duración: {selected.duration || Math.round((new Date(selected.end) - new Date(selected.start)) / 60000)} min
              </Typography>
            </>
          ) : (
            <Typography variant="body2">Selecciona un horario</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Volver</Button>
          <Button variant="contained" onClick={confirm} disabled={saving} startIcon={<AccessTimeIcon />}>
            {saving ? 'Guardando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PatientReschedulePage;
